import { useEffect, useState } from "react";
import BackofficeLayout, { Card, Btn } from "./BackofficeLayout";
import { api } from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, Edit } from "lucide-react";

const TYPES = ["SCI", "SAS", "HOLDING", "SARL", "AUTRE"];
const empty = { name: "", entity_type: "SCI", siret: "", address: "", notes: "", color: "#0F172A" };

export default function EntitiesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/backoffice/entities").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/backoffice/entities/${editing}`, form);
        toast.success("Entité mise à jour");
      } else {
        await api.post("/backoffice/entities", form);
        toast.success("Entité créée");
      }
      setForm(empty); setEditing(null); setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const startEdit = (e) => {
    setEditing(e.id);
    setForm({
      name: e.name, entity_type: e.entity_type,
      siret: e.siret || "", address: e.address || "",
      notes: e.notes || "", color: e.color || "#0F172A",
    });
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette entité ?")) return;
    try {
      await api.delete(`/backoffice/entities/${id}`);
      toast.success("Supprimée");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <BackofficeLayout
      title="Entités du groupe"
      subtitle="Structure juridique : SCI, SAS, holding. Sert de base pour la consolidation."
      actions={
        <Btn onClick={() => { setEditing(null); setForm(empty); setShowForm((s) => !s); }} data-testid="bof-entity-new-btn">
          <Plus className="w-3.5 h-3.5" /> Nouvelle entité
        </Btn>
      }
    >
      <Toaster theme="dark" position="top-right" />
      {showForm && (
        <Card className="mb-6" data-testid="bof-entity-form">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nom">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="input" data-testid="bof-entity-name" />
            </Field>
            <Field label="Type juridique">
              <select value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value })} className="input" data-testid="bof-entity-type">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="SIRET">
              <input value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} className="input" data-testid="bof-entity-siret" />
            </Field>
            <Field label="Adresse">
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input" data-testid="bof-entity-address" />
            </Field>
            <Field label="Couleur (KPI)">
              <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
                     className="h-10 w-24 bg-slate-800 border border-slate-700 rounded" />
            </Field>
            <Field label="Notes">
              <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" />
            </Field>
            <div className="md:col-span-2 flex gap-2">
              <Btn type="submit" data-testid="bof-entity-save">{editing ? "Enregistrer" : "Créer"}</Btn>
              <Btn type="button" variant="ghost" onClick={() => { setShowForm(false); setEditing(null); setForm(empty); }}>Annuler</Btn>
            </div>
          </form>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="bof-entities-list">
        {items.map((e) => (
          <Card key={e.id} data-testid={`entity-card-${e.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded" style={{ background: e.color || "#0F172A" }} />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{e.entity_type}</div>
                  <div className="font-bold text-white">{e.name}</div>
                  {e.siret && <div className="text-xs text-slate-500 mt-0.5">SIRET {e.siret}</div>}
                </div>
              </div>
              <div className="flex gap-1">
                <Btn variant="ghost" onClick={() => startEdit(e)} data-testid={`bof-entity-edit-${e.id}`}>
                  <Edit className="w-3.5 h-3.5" />
                </Btn>
                <Btn variant="danger" onClick={() => remove(e.id)} data-testid={`bof-entity-delete-${e.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Btn>
              </div>
            </div>
            {e.address && <div className="text-xs text-slate-400 mt-3">{e.address}</div>}
            {e.notes && <div className="text-xs text-slate-500 mt-2 italic">{e.notes}</div>}
          </Card>
        ))}
      </div>

      <style>{`
        .input {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgb(51, 65, 85);
          color: white;
          padding: 0.5rem 0.75rem;
          border-radius: 0.375rem;
          width: 100%;
          font-size: 0.875rem;
        }
        .input:focus { outline: 2px solid rgb(251, 191, 36); outline-offset: -1px; }
      `}</style>
    </BackofficeLayout>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1 block">{label}</span>
    {children}
  </label>
);
